import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { CheckCircle, Package, CreditCard, User, MapPin, ShoppingBag } from 'phosphor-react';

function OrderConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderData } = location.state || {};

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!orderData) {
      navigate('/');
    }
  }, [orderData, navigate]);

  if (!orderData) {
    return null; // or a loading spinner
  }

  const {
    order_number,
    total_amount,
    shipping_address,
    items,
    payment_status
  } = orderData;

  const customerName = shipping_address.first_name && shipping_address.last_name
    ? `${shipping_address.first_name} ${shipping_address.last_name}`
    : 'Guest';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          <div className="text-center mb-8">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" weight="fill" />
            <h1 className="text-3xl font-bold text-gray-900">Thank you for your order!</h1>
            <p className="text-gray-600 mt-2">
              Your order <span className="font-semibold text-ambre-600">#{order_number}</span> has been confirmed.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Package size={24} className="mr-3 text-ambre-600" />
              Order Summary
            </h2>
            <div className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="flex justify-between items-center text-sm">
                  <div>
                    <span className="font-semibold">{item.name}</span>
                    <span className="text-gray-500"> x {item.quantity}</span>
                  </div>
                  <span className="font-medium">€{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 mt-4 pt-4">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-ambre-600">€{total_amount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <MapPin size={20} className="mr-2 text-ambre-600" />
                Shipping Address
              </h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p className="font-medium">{customerName}</p>
                <p>{shipping_address.street_and_housenumber}</p>
                <p>{shipping_address.address_line_1}</p>
                {shipping_address.address_line_2 && <p>{shipping_address.address_line_2}</p>}
                <p>{shipping_address.city}, {shipping_address.postal_code}</p>
                <p>{shipping_address.country}</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <CreditCard size={20} className="mr-2 text-ambre-600" />
                Payment Information
              </h3>
              <div className="text-sm text-gray-600 space-y-2">
                <div className="flex justify-between">
                  <span>Payment Status:</span>
                  <span className="font-semibold text-green-600 capitalize">{payment_status}</span>
                </div>
                <div className="flex justify-between">
                  <span>Method:</span>
                  <span className="font-medium">Stripe / Credit Card</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/products"
              className="bg-ambre-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-ambre-700 transition-colors inline-flex items-center"
            >
              <ShoppingBag size={20} className="mr-2" />
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderConfirmation; 